<template>
  <!-- A hand-off: one piece of work passing from record to record (a timer becomes a time entry,
       then an invoice line, then a payment). When the strip scrolls into view each record rises
       in turn and a chip carries what moves along the wire to the next one. Record names and
       field labels are the app's own strings; values are sample data (translate="no"). The
       captions under each record are page copy. Resting styles are the final frame. -->
  <section class="sho">
    <div class="sho-shell">
      <header class="sho-heading">
        <div>
          <h2>{{ chapter.title }}</h2>
        </div>
        <p class="sho-intro">{{ chapter.intro }}</p>
      </header>

      <ol ref="rootEl" class="sho-strip" :class="{ 'is-run': running || armed, 'is-armed': armed }" :style="{ '--cols': columns }">
        <template v-for="(step, i) in chapter.steps" :key="step.kind">
          <li v-if="i > 0" class="sho-wire" aria-hidden="true" :style="{ '--d': `${i * 0.55}s` }">
            <span class="sho-line" />
            <span class="sho-run"><em translate="no">{{ step.carry }}</em></span>
          </li>
          <li class="sho-step" :style="{ '--d': `${i * 0.55 + (i ? 0.35 : 0)}s` }">
            <div class="sho-card saw" aria-hidden="true">
              <div class="sho-card-head">
                <span class="sho-icon" :style="{ '--tone': step.tone }"><svg viewBox="0 0 24 24"><path :d="step.icon" /></svg></span>
                <span class="sho-kind" translate="no">{{ step.kind }}</span>
                <em v-if="step.tag" class="saw-tag" :class="`saw-tag--${step.tag.tone}`" translate="no">{{ step.tag.text }}</em>
              </div>
              <b class="sho-title" translate="no">{{ step.title }}</b>
              <dl class="sho-lines" translate="no">
                <div v-for="line in step.lines" :key="line.k">
                  <dt>{{ line.k }}</dt>
                  <dd>{{ line.v }}</dd>
                </div>
              </dl>
            </div>
            <p class="sho-caption">{{ step.caption }}</p>
          </li>
        </template>
      </ol>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  // { eyebrow, title, intro, steps: [{ kind, icon, tone, title, tag: { text, tone }, lines: [{ k, v }], carry, caption }] }
  chapter: { type: Object, required: true },
});
// Records take equal columns with a fixed-width wire between each pair.
const columns = computed(() => props.chapter.steps.map(() => "minmax(0, 1fr)").join(" 4.5rem "));
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.3 });
</script>

<style scoped>
.sho { background: #fff; padding: 6.5rem 0; }
.dark .sho { background: #020617; }
.sho-shell { width: min(100% - 3rem, 84rem); margin: 0 auto; }
.sho-heading { display: grid; gap: 1.5rem; align-items: end; }
@media (min-width: 1024px) { .sho-heading { grid-template-columns: 1fr 1fr; gap: 2.5rem; } }
.sho-heading h2 { color: #020617; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.dark .sho-heading h2 { color: #f8fafc; }
.sho-intro { color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .sho-intro { color: #cbd5e1; }

/* Desktop: records and wires in one row. */
.sho-strip {
  display: grid;
  grid-template-columns: var(--cols);
  align-items: start;
  margin: 3rem 0 0;
  padding: 0;
  list-style: none;
}
.sho-step { display: grid; gap: 0.9rem; min-width: 0; }
.sho-card { height: auto; gap: 8px; padding: 12px 14px 14px; }
.sho-card-head { display: flex; align-items: center; gap: 8px; }
.sho-icon { display: grid; place-items: center; flex: none; width: 26px; height: 26px; border-radius: 6px; background: color-mix(in srgb, var(--tone) 14%, transparent); color: var(--tone); }
.sho-icon svg { width: 15px; height: 15px; }
.sho-kind { flex: 1; min-width: 0; color: var(--saw-muted); font-size: 12px; font-weight: 500; }
.sho-title { font-size: 15px; font-weight: 600; line-height: 1.3; }
.sho-lines { display: grid; gap: 4px; margin: 0; }
.sho-lines div { display: flex; justify-content: space-between; gap: 10px; font-size: 12px; }
.sho-lines dt { color: var(--saw-muted); }
.sho-lines dd { margin: 0; font-weight: 600; font-variant-numeric: tabular-nums; text-align: right; }
.sho-caption { color: #475569; font-size: 0.92rem; line-height: 1.6; }
.dark .sho-caption { color: #cbd5e1; }

.sho-wire { position: relative; height: 100%; min-height: 5rem; overflow: hidden; }
.sho-line { position: absolute; top: 3.1rem; right: 6px; left: 6px; height: 2px; border-radius: 1px; background: #cbd5e1; transform-origin: left center; }
.dark .sho-line { background: #334155; }
.sho-line::after { content: ""; position: absolute; top: -4px; right: -2px; border: 5px solid transparent; border-left-color: inherit; border-left: 7px solid #cbd5e1; }
.dark .sho-line::after { border-left-color: #334155; }
.sho-run { position: absolute; top: calc(3.1rem - 22px); left: 0; display: flex; justify-content: center; width: 100%; }
.sho-run em { border-radius: 4px; background: #0f172a; color: #e0f2fe; padding: 1px 5px; font-size: 10.5px; font-style: normal; font-weight: 700; white-space: nowrap; }
.dark .sho-run em { background: #e0f2fe; color: #0f172a; }

.is-run .sho-step { animation: sho-rise 0.6s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .sho-line { animation: sho-draw 0.45s ease-out var(--d) both; }
.is-run .sho-run { animation: sho-carry 0.6s cubic-bezier(0.33, 1, 0.68, 1) calc(var(--d) + 0.1s) both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes sho-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
@keyframes sho-draw { from { transform: scaleX(0); } to { transform: none; } }
@keyframes sho-carry { from { opacity: 0; transform: translateX(-60%); } to { opacity: 1; transform: none; } }

/* Tablets and phones: the records stack and the wires run down between them. */
@media (max-width: 960px) {
  .sho { padding: 4.5rem 0; }
  .sho-strip { grid-template-columns: minmax(0, 32rem); justify-content: start; }
  .sho-wire { height: 3.25rem; min-height: 0; }
  .sho-line { top: 6px; bottom: 10px; left: 1.75rem; right: auto; width: 2px; height: auto; transform-origin: center top; }
  .sho-line::after { top: auto; right: auto; bottom: -6px; left: -4px; border: 5px solid transparent; border-top: 7px solid #cbd5e1; }
  .dark .sho-line::after { border-left-color: transparent; border-top-color: #334155; }
  .sho-run { top: 50%; left: 2.75rem; justify-content: flex-start; width: auto; transform: translateY(-50%); }
  @keyframes sho-draw { from { transform: scaleY(0); } to { transform: none; } }
  @keyframes sho-carry { from { opacity: 0; transform: translateY(-80%); } to { opacity: 1; transform: translateY(-50%); } }
}
@media (max-width: 520px) {
  .sho-shell { width: min(100% - 2rem, 84rem); }
}
@media (prefers-reduced-motion: reduce) { .sho-strip * { animation: none !important; } }
</style>
